import * as THREE from 'three';
import { clipHullFrom } from '../physics/ClipHull.js';
import { MoverSet, serialiseMovers } from './Movers.js';

import { TextureFactory, SURFACES } from '../render/Materials.js';

/**
 * Split an undifferentiated spawn list into two opposing clusters.
 *
 * For maps whose source has no team information. Seeds on the farthest-apart
 * pair, then labels deterministically (lower average X becomes red) so the
 * sides do not swap between runs.
 *
 * The assignment is BALANCED, not nearest-seed. Nearest-seed is the obvious
 * implementation and it fails on exactly the maps this exists for: Crossfire's
 * spawns are not two tidy clusters, so it produced a 13/5 split whose larger
 * half sprawled across the middle of the map. Measured, that put opposing
 * players 12.8 m apart at spawn and teammates 47.8 m from each other — a spawn
 * you can be shot from, which is the whole thing this is meant to prevent.
 *
 * Sorting by how much a point PREFERS one seed over the other, then cutting the
 * list in half, keeps the sides the same size and the confident points at the
 * extremes. Only the genuinely ambiguous middle ground gets forced, which is
 * the right place to compromise.
 */
export function splitSpawns(points) {
  if (points.length < 2) return { red: [...points], blue: [...points] };

  let a = 0, b = 1, far = -1;
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const d = points[i].distanceToSquared(points[j]);
      if (d > far) { far = d; a = i; b = j; }
    }
  }

  const seedA = points[a], seedB = points[b];
  const ranked = points.map((p) => ({
    p, bias: p.distanceTo(seedA) - p.distanceTo(seedB),
  })).sort((m, n) => m.bias - n.bias);

  // Take a tight cluster at each end and DISCARD the ambiguous middle.
  //
  // Every point has to go somewhere is the wrong instinct here. A spawn in the
  // centre of the map is a bad team spawn by definition — whichever side owns
  // it starts in the open, next to the enemy — and forcing those points into a
  // side is what made each team span 60% of the map. Measured on Blackout, the
  // sides went from 79 m across to 34, and on Crossfire the nearest enemy at
  // spawn went from 9.6 m to 27. Fewer spawn points is a cheap price: the
  // crowding penalty in Room.spawn keeps players off each other.
  const take = Math.max(3, Math.round(ranked.length * 0.4));
  const A = ranked.slice(0, take).map((r) => r.p);
  const B = ranked.slice(-take).map((r) => r.p);

  const meanX = (arr) => arr.reduce((s, p) => s + p.x, 0) / Math.max(1, arr.length);
  return meanX(A) <= meanX(B) ? { red: A, blue: B } : { red: B, blue: A };
}

/**
 * "Crossfire" — a reconstruction of Dario Casali's Half-Life deathmatch map.
 *
 * The geometry is converted from the original Valve Hammer .rmf brush source:
 * every face's explicit vertex loop is triangulated, transformed from Hammer's
 * Z-up inch space into our Y-up metric space, and bucketed by texture name onto
 * our procedural PBR materials. Nothing from Valve ships here — only the layout
 * survives the conversion, rebuilt with our own surfaces and lighting.
 *
 * Exposes the same contract as Level so the game can swap between them.
 */
export class CrossfireLevel {
  static displayName = 'CROSSFIRE';
  static description = 'Half-Life · Dario Casali · 1998';
  /** Converted payload to fetch. Subclasses override to reuse this whole class. */
  static mapFile = 'crossfire.json';

  /**
   * Half-Life DM map, no bomb entities. Authored: A in the compound courtyard,
   * B by the bunker approach — 59.8 m apart and ~12 m from the nearest spawn on
   * each side. Subclasses override this with their own sites.
   */
  static bombSites = [
    { label: 'A', box: [-4.5, 4.1, -38.5, 4.5, 7.3, -29.5] },
    { label: 'B', box: [13.5, 7.9, 18.5, 22.5, 11.1, 27.5] },
  ];

  static async load(engine) {
    const res = await fetch(`${import.meta.env.BASE_URL ?? '/'}${this.mapFile}`);
    if (!res.ok) throw new Error(`${this.mapFile}: HTTP ${res.status}`);
    // `new this` so a subclass constructs itself, not the base.
    return new this(engine, await res.json());
  }

  constructor(engine, data) {
    this.engine = engine;
    this.data = data;
    /**
     * The map's own standing-player collision hull, when the payload carries
     * one. Not used for movement — see src/physics/ClipHull.js — only to tell
     * whether somebody has ended up where the map never allowed.
     */
    this.clipHull = clipHullFrom(data);
    this.root = new THREE.Group();
    this.collision = new THREE.Group();
    this.decor = new THREE.Group();
    this.root.add(this.collision, this.decor);

    this.tex = new TextureFactory(engine.renderer, 512);
    this._buildMaterials();
    this._buildGeometry();
    this._buildBrushEntities();
    this._buildLights();

    // Half-Life places an entity's origin at the centre of the player hull, not
    // at the feet — a 72-unit hull puts it 36 units (0.91 m) up. Our spawn API
    // takes a floor position, so drop them or everything spawns hovering.
    const EYE_TO_FLOOR = 36 * data.scale;
    this.spawnPoints = data.spawns.map(([x, y, z]) => new THREE.Vector3(x, y - EYE_TO_FLOOR, z));
    this.playerSpawn = this.spawnPoints[1] ?? this.spawnPoints[0];
    // Health chargers, weapon and ammo pickups sit at the places players
    // actually contest, so they double as decent AI cover/patrol anchors.
    this.coverPoints = data.poi.map(({ pos }) => new THREE.Vector3(pos[0], pos[1], pos[2]));
    this._buildBombData(EYE_TO_FLOOR);

    // Every BSP map gets its interiors lit from its own light-panel faces.
    // Subclasses that want more (Assault's hostage rooms) add to this.
    this.panelLights = this._lightFromEmissivePanels();

    this.root.traverse((o) => {
      if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; }
    });
  }

  /**
   * Hang a practical under every emissive light panel the map declares.
   *
   * GoldSrc baked interior lighting into lightmaps that the converter cannot
   * carry, so a BSP interior arrives as geometry with no light in it at all.
   * What DOES survive is which faces were light-panel textures — they come
   * through as emissive materials. Those faces are bright rectangles floating
   * in a black room: they read as lamps but illuminate nothing, which is
   * exactly how the Assault warehouse looked (mean luminance 25.7, crates
   * rendering pure black beside lit ceiling panels).
   *
   * So: cluster the emissive vertices into panels and put a real light under
   * each. The map tells us where its lamps are; we just have to believe it.
   */
  _lightFromEmissivePanels(budget = 22) {
    const defs = this.data.materials ?? {};
    const cells = new Map();          // 3 m cell -> accumulated panel centre

    for (const [key, bucket] of Object.entries(this.data.buckets ?? {})) {
      const def = defs[key];
      if (!def?.emissive) continue;
      const col = new THREE.Color(def.emissive);
      // Dim or very dark "emissive" faces are decals and screens, not lamps.
      if (col.r + col.g + col.b < 0.45) continue;
      const pos = bucket.position;
      const nor = bucket.normal;
      for (let i = 0; i < pos.length; i += 3) {
        // Only faces that throw light INTO the room: a lamp points away from
        // its own ceiling, so keep downward and side-facing panels.
        if (nor && nor[i + 1] > 0.5) continue;
        const x = pos[i], y = pos[i + 1], z = pos[i + 2];
        const k = `${Math.round(x / 3)},${Math.round(y / 3)},${Math.round(z / 3)}`;
        let c = cells.get(k);
        if (!c) cells.set(k, (c = { x: 0, y: 0, z: 0, n: 0, col, nx: 0, ny: 0, nz: 0 }));
        c.x += x; c.y += y; c.z += z; c.n++;
        if (nor) { c.nx += nor[i]; c.ny += nor[i + 1]; c.nz += nor[i + 2]; }
      }
    }

    // Biggest panels first, then cap: a hundred point lights would cost more
    // than the map is worth, and the largest fixtures are the ones that matter.
    const panels = [...cells.values()].sort((a, b) => b.n - a.n).slice(0, budget);
    for (const c of panels) {
      const px = c.x / c.n, py = c.y / c.n, pz = c.z / c.n;
      const nl = Math.hypot(c.nx, c.ny, c.nz) || 1;
      const light = new THREE.PointLight(c.col.getHex(), 7.5, 16, 2);
      // Sit just off the face so the fixture itself is not the thing being lit.
      light.position.set(px + (c.nx / nl) * 0.35,
        py + (c.ny / nl) * 0.35, pz + (c.nz / nl) * 0.35);
      this.decor.add(light);
    }
    return panels.length;
  }

  /**
   * One material per entry in the converted palette. Each Half-Life texture was
   * resolved offline to a base surface plus a tint approximating the original
   * WAD's albedo; identical results share a bucket, so 97 source textures come
   * out as ~29 draw calls that still carry Crossfire's colour separation.
   */
  _buildMaterials() {
    const T = this.tex;
    const base = {
      concrete: () => T.material('xf_concrete', SURFACES.concrete, { repeat: 1, normalScale: 1.0 }),
      sand: () => T.material('xf_sand', SURFACES.sand, { repeat: 1, normalScale: 1.0 }),
      metal: () => T.material('xf_metal', SURFACES.rustMetal, { repeat: 1, normalScale: 1.1 }),
      wood: () => T.material('xf_wood', SURFACES.concrete, { repeat: 1, normalScale: 1.3 }),
      /*
       * The converter classifies faces into more surfaces than this table used
       * to answer for, and anything it did not answer for fell through to
       * `base.concrete()` — tinted concrete wearing another material's colour.
       * On Assault that was **68 of its 144 materials**: every brick wall,
       * every painted steel door, all the gunmetal and all the plaster rendered
       * as concrete, which is why the map read flat and samey next to Dust II.
       * The presets already existed in `Materials.js`; nothing was wired to them.
       */
      brick: () => T.material('xf_brick', SURFACES.brick, { repeat: 1, normalScale: 1.15 }),
      plaster: () => T.material('xf_plaster', SURFACES.plaster, { repeat: 1, normalScale: 0.85 }),
      gunmetal: () => T.material('xf_gunmetal', SURFACES.gunmetal, { repeat: 1, normalScale: 1.0 }),
      paintedSteel: () => T.material('xf_painted', SURFACES.paintedSteel, { repeat: 1, normalScale: 0.9 }),
      asphalt: () => T.material('xf_asphalt', SURFACES.asphalt, { repeat: 1, normalScale: 1.0 }),
    };
    // Build each base surface once, then clone per tint — the texture set is the
    // expensive part and it is identical across tints.
    const cache = {};
    this.mat = {};

    for (const [key, def] of Object.entries(this.data.materials)) {
      if (def.surface === 'stripes') {
        this.mat[key] = new THREE.MeshStandardMaterial({
          map: this._stripeTexture(def.color),
          roughness: def.rough ?? 0.7,
          metalness: 0,
        });
        continue;
      }
      if (def.surface === 'flat') {
        // Light panels, hazard stripes, painted markings: flat colour, no
        // procedural surface, because in the original these are pure texture.
        this.mat[key] = new THREE.MeshStandardMaterial({
          color: def.color,
          roughness: def.rough ?? 0.8,
          metalness: 0,
          emissive: def.emissive ?? 0x000000,
          emissiveIntensity: def.emissiveIntensity ?? 0,
        });
        continue;
      }
      const src = (cache[def.surface] ??= base[def.surface]?.() ?? base.concrete());
      const m = src.clone();
      m.color = new THREE.Color(def.color);
      if (def.rough != null) m.roughness = def.rough;
      this.mat[key] = m;
    }
  }

  /** Diagonal yellow/black hazard stripes, as painted in the original. */
  _stripeTexture(color) {
    const S = 128;
    const c = document.createElement('canvas');
    c.width = c.height = S;
    const ctx = c.getContext('2d');
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    ctx.fillRect(0, 0, S, S);
    ctx.strokeStyle = '#16150f';
    ctx.lineWidth = S / 5.6;
    // Draw beyond the edges on both diagonals so the pattern tiles seamlessly.
    for (let i = -S; i < S * 2; i += S / 2.8) {
      ctx.beginPath();
      ctx.moveTo(i, -4);
      ctx.lineTo(i + S, S + 4);
      ctx.stroke();
    }
    // Scuff the paint so it doesn't read as vector art.
    ctx.globalAlpha = 0.14;
    for (let i = 0; i < 420; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#000' : '#fff';
      ctx.fillRect(Math.random() * S, Math.random() * S, 1 + Math.random() * 3, 1 + Math.random() * 2);
    }
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = this.tex.maxAniso;
    return tex;
  }

  _buildGeometry() {
    for (const [name, b] of Object.entries(this.data.buckets)) {
      if (!b.position.length) continue;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(b.position, 3));
      geo.setAttribute('normal', new THREE.Float32BufferAttribute(b.normal, 3));
      geo.setAttribute('uv', new THREE.Float32BufferAttribute(b.uv, 2));
      // aoMap needs a second UV channel; reuse the first.
      geo.setAttribute('uv1', new THREE.Float32BufferAttribute(b.uv, 2));
      geo.computeBoundingSphere();

      const def = this.data.materials[name];
      const mat = this.mat[name] ?? Object.values(this.mat)[0];
      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = `crossfire_${name}`;

      // Emissive light panels are recessed fittings — they shouldn't cast
      // shadows onto the surfaces they're meant to be lighting.
      if (def?.emissive) mesh.castShadow = false;
      this.collision.add(mesh);
    }
  }

  /* ------------------------------------------------- brush entities ----- */

  /**
   * Bomb sites and per-team spawns.
   *
   * Dust II carries both in its BSP (two `func_bomb_target` volumes named
   * `tgt_a`/`tgt_b`, and the CS convention of `info_player_start` = CT,
   * `info_player_deathmatch` = T). Maps without them declare a `static
   * bombSites` override in world space, and fall back to a deterministic
   * two-cluster split of the spawn list for teams.
   */
  _buildBombData(eyeToFloor) {
    const authored = this.constructor.bombSites;
    const fromData = this.data.bombSites ?? [];

    this.bombSites = (fromData.length ? fromData : (authored ?? [])).map((s, i) => {
      const min = new THREE.Vector3(...(s.min ?? s.box.slice(0, 3)));
      const max = new THREE.Vector3(...(s.max ?? s.box.slice(3, 6)));
      return {
        label: s.label ?? String.fromCharCode(65 + i),
        box: new THREE.Box3(min, max),
        center: new THREE.Vector3().addVectors(min, max).multiplyScalar(0.5),
      };
    });

    const ts = this.data.teamSpawns;
    const drop = (arr) => arr.map(([x, y, z]) => new THREE.Vector3(x, y - eyeToFloor, z));
    if (ts && ts.ct?.length && ts.t?.length) {
      this.teamSpawns = { blue: drop(ts.ct), red: drop(ts.t) };
    } else {
      this.teamSpawns = splitSpawns(this.spawnPoints);
    }
  }

  /** Register movers and ladders once the Game has built its Physics. */
  attach(physics) {
    for (const d of this.movers) physics.addMover(d);
    for (const box of this.ladders) physics.addLadder(box);
  }

  /**
   * Doors, lifts, buttons and ladders, rebuilt from the map's brush entities.
   *
   * Half-Life's func_door semantics, which this reimplements:
   *  - `angles` "pitch yaw roll" gives the move direction. Pitch -90 is up and
   *    +90 is down (the engine special-cases these); otherwise yaw is a compass
   *    bearing in the horizontal plane.
   *  - Travel distance is the brush's own size along that axis, minus `lip`.
   *  - `speed` is units/sec in Hammer units.
   *  - spawnflags bit 0 (1) means the door is authored in its OPEN position, so
   *    its rest state is the far end of the move and triggering closes it.
   *  - `wait` -1 means it never returns on its own.
   */
  _buildBrushEntities() {
    this.ladders = [];
    this._moverDefs = [];
    this._moverGroups = [];
    this._buttonDefs = [];

    for (const ent of this.data.brushEntities ?? []) {
      const { classname, props, bounds, geom } = ent;
      const min = new THREE.Vector3(...bounds.min);
      const max = new THREE.Vector3(...bounds.max);
      const box = new THREE.Box3(min, max);

      if (classname === 'func_ladder') {
        this.ladders.push(box);
        continue;
      }

      const group = new THREE.Group();
      /*
       * Tight boxes, one per geometry bucket.
       *
       * A mover collides as boxes, and until now that was ONE box covering the
       * whole entity. A door is a slab, so its union is the slab and nothing is
       * lost. A lift is a piston and a platform — a 0.8 m pole and a 3.25 m
       * plate 4 m above it — and the union of those two is a solid 3.25 x 4.47
       * x 3.25 block, 85% of which is empty space that collides. That is why
       * you could not walk under a raised platform, and why a descending lift
       * swallowed anyone standing where it was about to be.
       *
       * Per-bucket is not per-brush — buckets are material groups — but on
       * every mover in the map it separates exactly the parts that matter, and
       * the union is still kept for the broad phase.
       */
      const parts = [];
      for (const [name, b] of Object.entries(geom)) {
        if (!b.position?.length) continue;
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.Float32BufferAttribute(b.position, 3));
        g.setAttribute('normal', new THREE.Float32BufferAttribute(b.normal, 3));
        g.setAttribute('uv', new THREE.Float32BufferAttribute(b.uv, 2));
        g.setAttribute('uv1', new THREE.Float32BufferAttribute(b.uv, 2));
        g.computeBoundingSphere();
        const mesh = new THREE.Mesh(g, this.mat[name] ?? Object.values(this.mat)[0]);
        mesh.castShadow = mesh.receiveShadow = true;
        group.add(mesh);
        g.computeBoundingBox();
        parts.push(g.boundingBox.clone());
      }

      if (classname === 'func_door') {
        const move = this._doorMove(props, box);
        const startsOpen = (parseInt(props.spawnflags ?? '0', 10) & 1) !== 0;
        // Definitions only. The simulation is built once, below, by MoverSet —
        // the same class the SERVER runs, so a lift behaves identically on both
        // sides instead of existing on only one of them.
        this._moverDefs.push({
          name: props.targetname || '',
          home: [0, 0, 0],
          away: move.toArray(),
          speed: (parseFloat(props.speed) || 100) * this.data.scale,
          length: move.length(),
          wait: parseFloat(props.wait ?? '3'),
          startsOpen,
          box: [box.min.toArray(), box.max.toArray()],
          parts: parts.map((b2) => [b2.min.toArray(), b2.max.toArray()]),
        });
        this._moverGroups.push(group);
        this.decor.add(group);
        continue;
      }

      if (classname === 'func_button') {
        this.decor.add(group);
        this._buttonDefs.push({
          target: (props.target || '').replace(/_X$/, ''),
          centre: box.getCenter(new THREE.Vector3()).toArray(),
        });
        // Buttons are small and solid; fold them into the static collision set.
        this.collision.add(group.clone());
        continue;
      }

      // Everything else (walls, chargers, illusionary trim) is static.
      if (classname === 'func_illusionary') this.decor.add(group);
      else this.collision.add(group);
    }

    /**
     * One simulation, shared with the server.
     *
     * `movers` and `buttons` stay on the level because Physics, the HUD and
     * every probe reach for them there; what changed is that the state and the
     * tick now live in MoverSet rather than being a second implementation here.
     */
    this.sim = new MoverSet(this._moverDefs, this._buttonDefs);
    this.movers = this.sim.movers;
    this.buttons = this.sim.buttons;
    // targetname -> movers. Kept under its old name because probes and the HUD
    // both look it up here; it is the simulation's own index, not a copy.
    this.doors = this.sim.byName;
    // Meshes follow the simulation. They are parallel arrays by construction:
    // both are appended in the same loop, in the same order.
    this._moverGroups.forEach((g, i) => { this.movers[i].group = g; });
    this._syncMoverMeshes();
  }

  /** Move each mover's meshes to where the simulation says it is. */
  _syncMoverMeshes() {
    for (const m of this.movers) m.group?.position.copy(m.pos);
  }

  /** The mover and button definitions, for the server's map payload. */
  serialiseMovers() {
    return serialiseMovers(this.movers, this.buttons);
  }

  /** Move offset for a func_door, in metres. */
  _doorMove(props, box) {
    const [pitch, yaw] = (props.angles || '0 0 0').split(/\s+/).map(Number);
    const size = new THREE.Vector3();
    box.getSize(size);
    const lip = (parseFloat(props.lip) || 0) * this.data.scale;

    let axis;
    if (pitch === -90) axis = new THREE.Vector3(0, 1, 0);
    else if (pitch === 90) axis = new THREE.Vector3(0, -1, 0);
    else {
      // Hammer yaw is counter-clockwise from +X in its own XY plane, which maps
      // to our XZ plane as (cos, 0, -sin).
      const r = THREE.MathUtils.degToRad(yaw || 0);
      axis = new THREE.Vector3(Math.cos(r), 0, -Math.sin(r));
    }
    const extent = Math.abs(axis.x) * size.x + Math.abs(axis.y) * size.y + Math.abs(axis.z) * size.z;
    return axis.multiplyScalar(Math.max(0.05, extent - lip));
  }

  /**
   * Recompute one mover's boxes and move its meshes there.
   *
   * Thin now — the maths is `MoverSet.place` — but kept because probes drive a
   * mover by setting `t` directly and then need the world to catch up.
   */
  _applyDoor(d) {
    this.sim.place(d);
    d.group?.position.copy(d.pos);
  }

  /** Fire whatever a button targets. */
  trigger(targetName) {
    if (!this.sim.trigger(targetName)) return false;
    this.onTrigger?.(targetName);
    return true;
  }

  /**
   * Called when the player presses Use. Returns the button label if one fired,
   * so the HUD can acknowledge it.
   */
  use(position, maxDist = 2.6) {
    const best = this.sim.nearestButton(position, maxDist);
    if (!best || best.cooldown > 0) return null;
    best.cooldown = 1.0;
    return this.trigger(best.target) ? best.target : null;
  }

  /** Nearest usable button within range, for the HUD prompt. */
  nearestButton(position, maxDist = 2.6) {
    return this.sim.nearestButton(position, maxDist);
  }

  update(dt) {
    this.sim.update(dt);
    this._syncMoverMeshes();
  }

  _buildLights() {
    // The original map's light entities give us placement that matches the
    // architecture. They're point lights here, kept dim and short-range so they
    // read as practicals against the sun rather than competing with it.
    let budget = 10;
    for (const l of this.data.lights) {
      if (budget-- <= 0) break;
      const light = new THREE.PointLight(0xffddb0, 6, 16, 2);
      light.position.set(l.pos[0], l.pos[1], l.pos[2]);
      this.decor.add(light);
    }
  }
}
